import React from "react";
import { Dropdown } from "antd";
import { Link, useLocation, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import clsx from "clsx";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { BsChevronDoubleDown } from "react-icons/bs";
import { StyledBreadcrumb } from "./index.styled";
import { FaHome } from "react-icons/fa";
import { useTypedSelector } from "@lib/redux";
import { getRouteConfig } from "@app/.core/AppMenuRoutes";
import {
  findRouteById,
  findSelectedKeys,
  urlReplaceParams,
} from "@lib/helpers/UrlHelper";
import { IRouteConfig } from "@lib/types";
import useScreen from "@lib/hooks/useScreen";

export type AppBreadcrumbProps = {
  urlParams?: Record<string, string | JSX.Element>;
};

const AppBreadcrumb: React.FC<AppBreadcrumbProps> = ({ urlParams }) => {
  const { messages } = useIntl();
  const location = useLocation();
  const { user } = useTypedSelector((state) => state.auth);
  const params = useParams();
  const { isMobile } = useScreen();

  const routesConfig = getRouteConfig(user);
  const keys = findSelectedKeys({
    routes: routesConfig,
    path: location.pathname,
    params,
  });
  const menuItems = keys.found.map((key) => findRouteById(key, routesConfig));

  const MenuTitle = ({
    item,
    isSelected,
  }: {
    item: IRouteConfig;
    isSelected?: boolean;
  }) => {
    const title =
      urlParams?.[item?.paramKey] ||
      (messages[item?.messageId as keyof typeof messages] as string) ||
      item?.title;
    const url = urlReplaceParams(item?.url || "#", params);

    if (!item?.url)
      return (
        <StyledFlex style={{ gap: 10 }}>
          {item.icon && item.icon}
          {title}
        </StyledFlex>
      );

    return (
      <StyledFlex style={{ gap: 10 }}>
        {item.icon && item.icon}
        <Link
          to={url}
          className={clsx({ "selected-menu-item": isSelected })}
          style={{ color: "inherit", fontWeight: "bold" }}
        >
          {title}
        </Link>
      </StyledFlex>
    );
  };

  const buildMenuItems = (routes: any[]) => {
    return routes.map((route) => {
      if (route.dontShowInSidebar) return null;

      const children = route.children || [];
      const isSelected = keys.found.includes(route.id);
      return {
        key: route.id,
        label: <MenuTitle item={route} isSelected={isSelected} />,
        children:
          children.length > 0 && route.type !== "item"
            ? buildMenuItems(children)
            : undefined,
      };
    });
  };

  const homeMenuItems = buildMenuItems(routesConfig);

  const breadcrumbItems = menuItems
    .filter((item) => item.showInBottomNav !== false)
    .map((item) => {
      return {
        title: <MenuTitle item={item} />,
        key: item?.id,
      };
    });

  const lastItem = breadcrumbItems[breadcrumbItems.length - 1];

  return (
    <StyledBreadcrumb
      items={[
        isMobile
          ? {}
          : {
              title: (
                <Dropdown
                  menu={{
                    items: homeMenuItems,
                    selectedKeys: keys.found,
                    defaultOpenKeys: keys.found.slice(0, -1),
                  }}
                  trigger={["hover"]}
                  placement="bottom"
                >
                  <StyledFlex style={{ gap: 5 }}>
                    <FaHome />
                    <BsChevronDoubleDown />
                  </StyledFlex>
                </Dropdown>
              ),
            },
      ].concat(isMobile ? [lastItem] : breadcrumbItems)}
    />
  );
};

export default AppBreadcrumb;
